import React, { useEffect, useState, useCallback, useRef } from "react";
import DraggerHandle from "../../assets/images/gripHandle.svg";
import styles from "./BidToolConfigurationView.module.scss";
import { Row, Col, Modal, notification } from "antd";
import { ButtonShape, ButtonSize } from "../../enums/ButtonAttributesEnums";
import {
  CancelStyles,
  SubmitStyles,
  BidConfigFormProps,
  stepOneKeys,
  DOLLAR_PREFIX,
  BASE_TRUCK_ASSUMPTIONS_HEADER,
  bidToolResetModalConstants,
  fieldNames,
  DRAG_HANDLE_ALT,
} from "./constants";
import TNButton from "../../reusable-components/TNButton";
import InputField from "../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../enums/InputFieldAttributesEnums";
import BidToolConfigContainer from "./BidToolConfigContainer";
import { BidToolSettingsConfig } from "../../types/getSettingsBidConfigDataObject";
import { useFormContext } from "react-hook-form";
import { BidConfigPayloadFormatter } from "../../utils/bidConfigPayloadFormatter";
import { stripCurrencyFormatting } from "../../Utilities/regex";
import BidToolConfigServices from "../../services/bidToolConfig.service";
import DraggableRow from "../../reusable-components/DraggableRow";

export interface TruckAssumptionsContentProps {
  initialValues: BidToolSettingsConfig;
  currentTab?: string;
  handleSaveOption?: (val: boolean) => void;
  handleValidationErrors?: (error: any) => void;
}

const TruckAssumptionsContent: React.FC<TruckAssumptionsContentProps> = ({
  initialValues,
  currentTab,
  handleSaveOption,
  handleValidationErrors,
}) => {
  const { watch, setValue, trigger, handleSubmit, clearErrors } =
    useFormContext();

  const { CANCEL_BTN_TXT, SUBMIT_BTN_TXT } = BidConfigFormProps;
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const containerFields = initialValues?.data?.[0]?.baseTruckAssumption;
  const currentFieldValues = watch();

  const { SubmitSettingsBidConfig } = BidToolConfigServices();
  const { mutateAsync: stepOneSubmitMutate, isPending } =
    SubmitSettingsBidConfig();

  const [rows, setRows] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (containerFields) {
      const initialRows = containerFields?.map((field, index) => ({
        id: field.truckType || `row-${index}`,
        index,
        truckType: field.truckType,
        rate: field.rate,
        biddingTons: field.biddingTons,
        originalIndex: index,
      }));
      setRows(initialRows);
      setOriginalOrder(initialRows?.map((row) => row?.id));
    }
  }, [containerFields]);

  useEffect(() => {
    if (hasOrderChanged && rows?.length > 0) {
      const currentFormValues = { ...currentFieldValues };
      rows.forEach((row, newIndex) => {
        const rateFieldName = `${row?.truckType}_${fieldNames.CONFIG_RATE}`;
        const tonsFieldName = `${row?.truckType}_${fieldNames.BIDDING_CONFIG_TONS}`;
      });
    }
  }, [rows, hasOrderChanged]);

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      setRows((prevRows) => {
        const newRows = [...prevRows];
        const [removed] = newRows.splice(dragIndex, 1);
        newRows.splice(hoverIndex, 0, removed);
        const updatedRows = newRows?.map((row, idx) => ({
          ...row,
          index: idx,
        }));

        const currentOrder = updatedRows?.map((row) => row?.id);
        const hasChanged =
          JSON.stringify(currentOrder) !== JSON.stringify(originalOrder);
        setHasOrderChanged(hasChanged);

        return updatedRows;
      });
    },
    [originalOrder]
  );

  const getOrderedPayload = useCallback(
    (fieldValues) => {
      const basePayload = BidConfigPayloadFormatter({
        payloadData: fieldValues,
        id: initialValues?.data?.[0]?.id,
      });

      if (!hasOrderChanged) {
        return basePayload;
      }

      const reorderedAssumptions = rows
        ?.map((row) => {
          const originalAssumption = containerFields?.find(
            (assumption) => assumption?.truckType === row?.truckType
          );

          if (originalAssumption) {
            return {
              ...originalAssumption,
              rate: fieldValues[`${row?.truckType}_${fieldNames.CONFIG_RATE}`],
              biddingTons:
                fieldValues[
                  `${row?.truckType}_${fieldNames.BIDDING_CONFIG_TONS}`
                ],
            };
          }
          return null;
        })
        .filter(Boolean);

      return {
        ...basePayload,
        baseTruckAssumption: reorderedAssumptions,
        orderChanged: true,
      };
    },
    [rows, hasOrderChanged, initialValues, containerFields]
  );

  const handleStepOneSubmit = async () => {
    const orderedPayload = getOrderedPayload(currentFieldValues);

    await stepOneSubmitMutate(orderedPayload, {
      onSuccess: () => {
        setIsSuccessOpen(true);
        setHasOrderChanged(false);
        setOriginalOrder(rows?.map((row) => row?.id));
      },
    });
  };

  const handleBlur =
    (fieldName: string) => (event: React.FocusEvent<HTMLInputElement>) => {
      const value = parseFloat(stripCurrencyFormatting(event.target.value));
      if (!isNaN(value)) {
        const roundedValue = value.toFixed(2);
        setValue(fieldName, roundedValue);
      }
      trigger(fieldName);
    };

  const resetStepValues = () => {
    containerFields?.forEach((value) => {
      setValue(`${value?.truckType}_${fieldNames.CONFIG_RATE}`, value?.rate);
      setValue(
        `${value?.truckType}_${fieldNames.BIDDING_CONFIG_TONS}`,
        value?.biddingTons
      );
    });

    if (containerFields) {
      const initialRows = containerFields?.map((field, index) => ({
        id: field.truckType || `row-${index}`,
        index,
        truckType: field.truckType,
        rate: field.rate,
        biddingTons: field.biddingTons,
        originalIndex: index,
      }));
      setRows(initialRows);
      setOriginalOrder(initialRows?.map((row) => row?.id));
      setHasOrderChanged(false);
    }
  };

  const handleOpenWarningModal = () => setIsWarningModalOpen(true);
  const handleCloseWarningModal = () => setIsWarningModalOpen(false);

  useEffect(() => {
    if (isSuccessOpen) {
      handleSaveOption(isSuccessOpen);
      setIsSuccessOpen(false);
    }
  }, [isSuccessOpen]);

  const renderRowContent = (row) => (
    <div className={styles.RowContainer} key={row.id}>
      <Row style={{ alignItems: "center", justifyContent: "start" }}>
        <Col span={1}>
          <span>
            <img src={DraggerHandle} alt={DRAG_HANDLE_ALT} />
          </span>
        </Col>
        <Col span={7}>
          <div className={styles.dragHandle}>{row.truckType}</div>
        </Col>
        <Col span={8}>
          <div className={styles.contentContainer}>
            <InputField
              customSmallErrorText
              fixedDecimalScale={true}
              formattedNumberType
              name={`${row.truckType}_${fieldNames.CONFIG_RATE}`}
              type={InputfieldTypes.TEXT}
              prefix={DOLLAR_PREFIX}
              onBlur={handleBlur(`${row.truckType}_${fieldNames.CONFIG_RATE}`)}
            />
          </div>
        </Col>
        <Col span={8}>
          <div className={styles.contentContainer}>
            <InputField
              customSmallErrorText
              small
              name={`${row.truckType}_${fieldNames.BIDDING_CONFIG_TONS}`}
              type={InputfieldTypes.TEXT}
              onBlur={() =>
                trigger(`${row.truckType}_${fieldNames.BIDDING_CONFIG_TONS}`)
              }
            />
          </div>
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      <Modal
        visible={isWarningModalOpen}
        onCancel={handleCloseWarningModal}
        title={bidToolResetModalConstants.TITLE_TEXT}
        okText={bidToolResetModalConstants.CONFIRM_TEXT}
        onOk={() => {
          resetStepValues();
          clearErrors(stepOneKeys(initialValues));
          notification.success({
            message: bidToolResetModalConstants.SUCCESS_MESSAGE,
          });
          handleCloseWarningModal();
        }}
      >
        <p style={bidToolResetModalConstants.MODAL_MESSAGE_STYLE}>
          {bidToolResetModalConstants.CONFIRM_MESSAGE}
        </p>
      </Modal>
      <div className={styles.TruckAssumptionWrapper}>
        <div className={styles.ContentContainer}>
          <Row>
            <BidToolConfigContainer
              containerOne
              small
              headerTitles={BASE_TRUCK_ASSUMPTIONS_HEADER}
            >
              <div ref={scrollContainerRef} className={styles.scrollContainer}>
                {rows?.map((row, index) => (
                  <DraggableRow
                    key={row.id}
                    id={row.id}
                    index={index}
                    moveRow={moveRow}
                    scrollContainerRef={scrollContainerRef}
                  >
                    {renderRowContent(row)}
                  </DraggableRow>
                ))}
              </div>
            </BidToolConfigContainer>
          </Row>
          <Row className={styles.ButtonRow}>
            <DraggableRow isButtonRow={true}>
              <div className={styles.buttonWrapper}>
                <TNButton
                  shape={ButtonShape.DEFAULT}
                  size={ButtonSize.LARGE}
                  text={CANCEL_BTN_TXT}
                  customStyles={CancelStyles}
                  onClick={handleOpenWarningModal}
                  disabled={isPending}
                />
                <TNButton
                  shape={ButtonShape.DEFAULT}
                  size={ButtonSize.LARGE}
                  text={SUBMIT_BTN_TXT}
                  customStyles={SubmitStyles}
                  onClick={handleSubmit(
                    handleStepOneSubmit,
                    handleValidationErrors
                  )}
                  loading={isPending}
                />
              </div>
            </DraggableRow>
          </Row>
        </div>
      </div>
    </>
  );
};

export default TruckAssumptionsContent;
